import React from 'react';

/**
 * Partners strip — used two ways across the site.
 *
 * `variant`
 *  - "inline" : Editorial partners ribbon under the Home hero. Deliberately
 *               understated — no section heading, no cards, no CTA. Logos sit
 *               directly on the page background, prefaced by a small italic
 *               "with the support of" note (à la D&AD, One Show, Cannes).
 *
 *  - "strip"  : Panel above the footer on every non-Home page. Slightly more
 *               structured with an eyebrow label + walnut-brown separator
 *               rules. Still calm and unobtrusive.
 *
 * Colours: on the site's cream editorial base we use a walnut-brown for
 * every text element so labels stay legible. Logos are boxed on white cards
 * (matched heights) so the dark Rawbare wordmark and the multicolour
 * Teamology wordmark read consistently.
 */
const PARTNERS = [
  {
    label: 'Communication Partner',
    name: 'Teamology Media',
    logo: '/partners/teamology-media.png',
    url: 'https://theteamology.com/',
  },
  {
    label: 'Lifestyle Partner',
    name: 'Rawbare',
    logo: '/partners/rawbare-black.png',
    url: 'https://www.rawbare.com/',
  },
  {
    label: 'Gifting Partner',
    name: 'Plum',
    logo: '/partners/plum.png',
    url: 'https://plumgoodness.com/',
  },
];

const tileHover = 'transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_18px_rgba(58,36,24,0.10)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C9A56C]';

const logoHeight = (name, size) => {
  if (name === 'Rawbare') return size === 'strip' ? 'h-5 md:h-6' : 'h-4 md:h-5';
  if (name === 'Plum') return size === 'strip' ? 'h-10 md:h-12' : 'h-9 md:h-10';
  return size === 'strip' ? 'h-8 md:h-10' : 'h-7 md:h-9';
};

const PartnersSection = ({ variant = 'strip' }) => {
  if (variant === 'inline') return <PartnersInline />;
  return <PartnersStrip />;
};

/** Home page — curated partners ribbon under hero. Editorial, in-brand.
 *  Colour logos on ivory cream tiles with gold hairlines and small partner
 *  labels; sits between soft-gold rule lines like a small honour roll rather
 *  than a promotional block. */
const PartnersInline = () => (
  <div
    className="relative z-[2] py-12 md:py-14 border-y border-[#3A2418]/8"
    data-testid="partners-section-inline"
  >
    <div className="content-container">
      <div className="flex items-center justify-center gap-3 mb-8 md:mb-10">
        <span aria-hidden className="h-px w-8 md:w-10 bg-[#C9A56C]/55" />
        <div className="font-serif text-[13px] md:text-[14px] text-[#3A2418]/75">
          In Partnership With
        </div>
        <span aria-hidden className="h-px w-8 md:w-10 bg-[#C9A56C]/55" />
      </div>

      <div className="flex flex-wrap items-stretch justify-center gap-x-6 md:gap-x-10 gap-y-8">
        {PARTNERS.map((p, i) => (
          <React.Fragment key={p.name}>
            <div
              className="flex flex-col items-center text-center max-w-[260px]"
              data-testid={`partner-inline-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="text-[9.5px] md:text-[10px] tracking-[0.32em] uppercase text-[#3A2418]/55 mb-3">
                {p.label}
              </div>
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                aria-label={`${p.name} — ${p.label} (opens in new tab)`}
                data-testid={`partner-inline-link-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
                className={`inline-flex items-center justify-center bg-[#FAF3E7] border border-[#C9A56C]/35 px-7 py-4 md:px-9 md:py-5 min-h-[76px] md:min-h-[84px] min-w-[200px] md:min-w-[240px] shadow-[0_0_0_1px_rgba(58,36,24,0.02),0_1px_2px_rgba(58,36,24,0.04)] ${tileHover}`}
              >
                <img
                  src={p.logo}
                  alt={p.name}
                  loading="lazy"
                  decoding="async"
                  className={`${logoHeight(p.name, 'inline')} w-auto object-contain`}
                />
              </a>
              <div className="font-serif text-[12.5px] md:text-[13px] text-[#3A2418]/85 mt-3">
                {p.name}
              </div>
            </div>
            {/* gold divider between partners on desktop */}
            {i < PARTNERS.length - 1 && (
              <span
                aria-hidden
                className="hidden md:block self-center h-16 w-px bg-gradient-to-b from-transparent via-[#C9A56C]/40 to-transparent"
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  </div>
);

/** Interior pages — structured strip above footer, calm palette. */
const PartnersStrip = () => (
  <section
    className="relative z-[2] py-12 md:py-14 border-t border-[#3A2418]/10 bg-[#F1E7D6]/40"
    data-testid="partners-section-strip"
    aria-labelledby="partners-heading"
  >
    <div className="content-container">
      <div className="flex items-center justify-center gap-3 mb-8">
        <span aria-hidden className="h-px w-8 bg-[#3A2418]/25" />
        <div
          id="partners-heading"
          className="text-[10px] md:text-[10.5px] tracking-[0.35em] uppercase text-[#3A2418]/70"
        >
          Sponsors &amp; Partners
        </div>
        <span aria-hidden className="h-px w-8 bg-[#3A2418]/25" />
      </div>

      <div className="flex flex-wrap items-stretch justify-center gap-6 md:gap-10">
        {PARTNERS.map((p) => (
          <div
            key={p.name}
            className="flex flex-col items-center text-center min-w-[200px]"
            data-testid={`partner-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#3A2418]/60 mb-3">
              {p.label}
            </div>
            <a
              href={p.url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              aria-label={`${p.name} — ${p.label} (opens in new tab)`}
              data-testid={`partner-link-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
              className={`inline-flex items-center justify-center bg-white border border-[#3A2418]/10 px-6 py-4 md:px-8 md:py-5 min-h-[76px] md:min-h-[88px] min-w-[220px] md:min-w-[240px] ${tileHover}`}
            >
              <img
                src={p.logo}
                alt={p.name}
                width="240"
                height="60"
                loading="lazy"
                decoding="async"
                className={`${logoHeight(p.name, 'strip')} w-auto object-contain`}
              />
            </a>
            <div className="text-[12.5px] font-serif text-[#3A2418]/80 mt-3">
              {p.name}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default PartnersSection;
